import {
  Breakpoint,
  Button as MuiButton,
  ButtonProps,
  IconButton as MuiIconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { mergeSx } from './merge-sx';
import withClickTracking from './withClickTracking';

interface Props extends Omit<ButtonProps, 'color'> {
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | undefined;
  /**
   * Render as the startIcon alone below this breakpoint and labelled at or
   * above it, for a row of actions that would otherwise wrap on a narrow
   * screen. A button without a startIcon stays labelled.
   */
  collapseBelow?: Breakpoint;
  /**
   * Settle the question rather than letting the width answer it: true renders
   * the startIcon alone at every width, false keeps the label. Left out,
   * collapseBelow decides.
   */
  asIcon?: boolean;
}

type ShapeProps = Omit<Props, 'collapseBelow' | 'asIcon'>;

const validColors = ['inherit', 'primary', 'secondary', 'success', 'error', 'info', 'warning'];

const LabelledButton = ({ color = 'primary', ...rest }: ShapeProps) => {
  const buttonColor = validColors.includes(color) ? color : 'primary';
  return (
    <MuiButton
      color={buttonColor}
      {...rest}
      sx={mergeSx(rest.sx ?? {}, (theme) => ({
        fontWeight: theme.typography.button.fontWeight,
        fontFamily: theme.typography.button.fontFamily,
      }))}
    />
  );
};

const IconOnlyButton = ({
  startIcon,
  children,
  color = 'primary',
  // Shape props a labelled button has and an icon button doesn't.
  variant: _variant,
  endIcon: _endIcon,
  fullWidth: _fullWidth,
  disableElevation: _disableElevation,
  loadingPosition: _loadingPosition,
  ...rest
}: ShapeProps) => (
  // MUI's own, not ./IconButton: the outer Button already tracks the click.
  <MuiIconButton
    aria-label={typeof children === 'string' ? children : undefined}
    color={validColors.includes(color) ? color : 'primary'}
    {...rest}
  >
    {startIcon}
  </MuiIconButton>
);

const CollapsingButton = ({ collapseBelow, ...rest }: ShapeProps & { collapseBelow: Breakpoint }) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down(collapseBelow));
  return isNarrow ? <IconOnlyButton {...rest} /> : <LabelledButton {...rest} />;
};

const Button = withClickTracking(({ collapseBelow, asIcon, ...rest }: Props) => {
  // Only a button that asks to collapse subscribes to the media query.
  if (!rest.startIcon || asIcon === false) return <LabelledButton {...rest} />;
  if (asIcon) return <IconOnlyButton {...rest} />;
  if (collapseBelow) return <CollapsingButton collapseBelow={collapseBelow} {...rest} />;
  return <LabelledButton {...rest} />;
});

export default Button;
